import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Eyebrow, SectionHeading } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { useDialogs } from "@/components/GlobalDialogs";
import { getCourses, getEarn } from "@/lib/api";

const CourseCard = ({ c }) => (
  <Link to={`/courses/${c.slug}`} data-testid={`course-card-${c.slug}`} className="group block h-full">
    <Card className="card-lift h-full overflow-hidden rounded-2xl border-border bg-card hover:border-primary/40">
      <img src={c.image} alt={c.title} className="aspect-[16/10] w-full object-cover" />
      <div className="p-5">
        <div className="flex items-center gap-2">
          <Badge className={`rounded-full ${c.kind === "free" ? "bg-accent text-accent-foreground" : ""}`}>{c.kind === "free" ? "Free" : `₹${c.price}`}</Badge>
          <span className="text-xs text-muted-foreground">{c.level}</span>
        </div>
        <h3 className="mt-3 font-display text-lg font-semibold">{c.title}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.summary}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>{c.duration} · {c.format}</span>
          <span className="font-medium text-primary transition-transform group-hover:translate-x-1">View →</span>
        </div>
      </div>
    </Card>
  </Link>
);

export default function LearnEarn() {
  const { openWaitlist, openCollaborate } = useDialogs();
  const [courses, setCourses] = useState([]);
  const [earn, setEarn] = useState([]);

  useEffect(() => {
    getCourses().then(setCourses).catch(() => {});
    getEarn().then((d) => setEarn(d.categories || [])).catch(() => {});
  }, []);

  const free = courses.filter((c) => c.kind === "free");
  const paid = courses.filter((c) => c.kind === "paid");

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border noise">
        <div className="ytca-container py-16 sm:py-20">
          <Eyebrow>Learn & Earn</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold tracking-tight sm:text-6xl">Learn a skill. Build your identity. Find opportunities to use it.</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">Two journeys, one belief: everyone can make. Start by learning — then turn your skill into real opportunities.</p>
        </div>
      </section>

      <div className="ytca-container py-12">
        <Tabs defaultValue="learn" className="w-full">
          <TabsList className="rounded-full">
            <TabsTrigger value="learn" className="rounded-full" data-testid="tab-learn">Learn</TabsTrigger>
            <TabsTrigger value="earn" className="rounded-full" data-testid="tab-earn">Earn</TabsTrigger>
          </TabsList>

          <TabsContent value="learn" className="mt-10">
            <SectionHeading eyebrow="Free Courses & Workbooks" title="Start for free" intro="Download a workbook and begin today — no cost, no catch." />
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {free.map((c, i) => <Reveal key={c.slug} delay={i * 0.05}><CourseCard c={c} /></Reveal>)}
            </div>

            <div className="mt-16">
              <SectionHeading eyebrow="Paid Courses" title="Go deeper with guided courses" intro="Structured courses taught by artists and the founder, with live sessions and feedback." />
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {paid.map((c, i) => <Reveal key={c.slug} delay={i * 0.05}><CourseCard c={c} /></Reveal>)}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="earn" className="mt-10">
            <SectionHeading eyebrow="Earn With YTCA" title="An opportunity ecosystem for creatives" intro="For people who already have creative skills and want opportunities to use or monetise them." />
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {earn.map((c, i) => (
                <Reveal key={c.key} delay={i * 0.03}>
                  <Card className="h-full rounded-2xl border-border bg-card p-5">
                    <h3 className="font-display text-lg font-semibold">{c.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{c.description}</p>
                  </Card>
                </Reveal>
              ))}
            </div>

            <div className="mt-12 grid gap-6 rounded-3xl border border-border bg-secondary/40 p-8 md:grid-cols-[1.2fr_0.8fr] md:items-center">
              <div>
                <h3 className="font-display text-2xl font-semibold">Ready to put your skills to work?</h3>
                <p className="mt-2 text-muted-foreground">Join the waitlist for opportunities, or reach out to collaborate with YTCA directly.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Button onClick={() => openWaitlist("Earn opportunities")} className="rounded-full" data-testid="earn-join-waitlist-button">Join Waitlist</Button>
                <Button onClick={openCollaborate} variant="outline" className="rounded-full" data-testid="earn-collaborate-button">Collaborate With Us</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
